import { useTranslation } from 'react-i18next';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const emptySlot = () => ({
  scheduleType: 'weekly',
  dayOfWeek: 1,
  date: '',
  startTime: '09:00',
  endTime: '17:00',
});

export const toEditorSchedules = (branchSchedules = []) =>
  branchSchedules.map((row) => ({
    branchId: row.branchId?.toString?.() || String(row.branchId || ''),
    slots: (row.slots || []).map((slot) => ({
      scheduleType: slot.date ? 'date' : 'weekly',
      dayOfWeek: slot.dayOfWeek ?? 1,
      date: slot.date ? new Date(slot.date).toISOString().split('T')[0] : '',
      startTime: slot.startTime || '09:00',
      endTime: slot.endTime || '17:00',
    })),
  }));

export const toApiSchedules = (schedules = []) =>
  schedules
    .filter((row) => row.branchId)
    .map((row) => ({
      branchId: row.branchId,
      slots: (row.slots || [])
        .map((slot) => {
          const base = { startTime: slot.startTime, endTime: slot.endTime };
          if (slot.scheduleType === 'date' && slot.date) {
            return { ...base, date: slot.date };
          }
          if (slot.scheduleType === 'weekly') {
            return { ...base, dayOfWeek: Number(slot.dayOfWeek) };
          }
          return null;
        })
        .filter(Boolean),
    }))
    .filter((row) => row.slots.length);

const StaffBranchScheduleEditor = ({ branches = [], value = [], onChange }) => {
  const { t } = useTranslation();

  const addBranch = () => {
    const used = new Set(value.map((row) => row.branchId));
    const nextBranch = branches.find((b) => !used.has(b._id));
    if (!nextBranch) return;
    onChange([...value, { branchId: nextBranch._id, slots: [emptySlot()] }]);
  };

  const updateBranch = (index, branchId) => {
    const next = [...value];
    next[index] = { ...next[index], branchId };
    onChange(next);
  };

  const removeBranch = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const addSlot = (branchIndex) => {
    const next = [...value];
    next[branchIndex] = {
      ...next[branchIndex],
      slots: [...(next[branchIndex].slots || []), emptySlot()],
    };
    onChange(next);
  };

  const updateSlot = (branchIndex, slotIndex, patch) => {
    const next = [...value];
    const slots = [...(next[branchIndex].slots || [])];
    slots[slotIndex] = { ...slots[slotIndex], ...patch };
    next[branchIndex] = { ...next[branchIndex], slots };
    onChange(next);
  };

  const removeSlot = (branchIndex, slotIndex) => {
    const next = [...value];
    const slots = (next[branchIndex].slots || []).filter((_, i) => i !== slotIndex);
    next[branchIndex] = { ...next[branchIndex], slots };
    onChange(next);
  };

  const unusedBranches = branches.filter(
    (b) => !value.some((row) => row.branchId === b._id)
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
          {t('staff.branchSchedules.title')}
        </p>
        <p className="mt-1 text-xs text-zinc-500">{t('staff.branchSchedules.hint')}</p>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-zinc-500">{t('staff.branchSchedules.empty')}</p>
      )}

      {value.map((row, branchIndex) => (
        <div
          key={`${row.branchId || 'new'}-${branchIndex}`}
          className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3"
        >
          <div className="mb-3 flex items-center gap-2">
            <select
              value={row.branchId}
              onChange={(e) => updateBranch(branchIndex, e.target.value)}
              className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
            >
              <option value="">{t('staff.branchSchedules.selectBranch')}</option>
              {branches.map((branch) => (
                <option
                  key={branch._id}
                  value={branch._id}
                  disabled={value.some((r, i) => i !== branchIndex && r.branchId === branch._id)}
                >
                  {branch.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeBranch(branchIndex)}
              className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-2 py-1 text-xs text-zinc-500"
            >
              {t('common.remove')}
            </button>
          </div>

          <div className="space-y-2">
            {(row.slots || []).map((slot, slotIndex) => (
              <div
                key={slotIndex}
                className="grid gap-2 rounded-md bg-zinc-50 dark:bg-zinc-950/50 p-2 sm:grid-cols-2"
              >
                <select
                  value={slot.scheduleType}
                  onChange={(e) => updateSlot(branchIndex, slotIndex, { scheduleType: e.target.value })}
                  className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
                >
                  <option value="weekly">{t('staff.branchSchedules.recurring')}</option>
                  <option value="date">{t('staff.branchSchedules.specificDate')}</option>
                </select>

                {slot.scheduleType === 'weekly' ? (
                  <select
                    value={slot.dayOfWeek}
                    onChange={(e) => updateSlot(branchIndex, slotIndex, { dayOfWeek: Number(e.target.value) })}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
                  >
                    {DAY_KEYS.map((key, dow) => (
                      <option key={key} value={dow}>
                        {t(`staff.branchSchedules.days.${key}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="date"
                    value={slot.date}
                    onChange={(e) => updateSlot(branchIndex, slotIndex, { date: e.target.value })}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
                  />
                )}

                <input
                  type="time"
                  value={slot.startTime}
                  onChange={(e) => updateSlot(branchIndex, slotIndex, { startTime: e.target.value })}
                  className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
                />
                <input
                  type="time"
                  value={slot.endTime}
                  onChange={(e) => updateSlot(branchIndex, slotIndex, { endTime: e.target.value })}
                  className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
                />

                <button
                  type="button"
                  onClick={() => removeSlot(branchIndex, slotIndex)}
                  className="text-left text-xs text-zinc-500 sm:col-span-2"
                >
                  {t('staff.branchSchedules.removeSlot')}
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => addSlot(branchIndex)}
            className="mt-2 text-xs text-glow-400"
          >
            {t('staff.branchSchedules.addSlot')}
          </button>
        </div>
      ))}

      {unusedBranches.length > 0 && (
        <button
          type="button"
          onClick={addBranch}
          className="text-sm text-glow-400"
        >
          {t('staff.branchSchedules.addBranch')}
        </button>
      )}
    </div>
  );
};

export default StaffBranchScheduleEditor;
